/**
 * Submission History tab. Renders the table from the AJAX payload.
 */

import { ApiError, api } from '../api.js';
import { t } from '../config.js';
import { byHook, hide, show } from '../dom.js';

const STATUS_META = {
    0: { className: 'marc__badge--pending',  key: 'statusPending'  },
    1: { className: 'marc__badge--approved', key: 'statusApproved' },
    2: { className: 'marc__badge--rejected', key: 'statusRejected' },
    4: { className: 'marc__badge--excluded', key: 'statusExcluded' },
};

const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return value;
    return d.toISOString().slice(0, 10);
};

const renderRow = (item, { onResubmit }) => {
    const tr = document.createElement('tr');
    tr.className = 'marc__history-row';

    const meta = STATUS_META[item.status] ?? { className: '', key: 'statusPending' };

    const cells = [
        { text: item.order_number || '—' },
        { text: item.product_title || '—', attrs: { title: item.product_title || '' } },
        { text: formatDate(item.created_at) },
        {
            html: `<span class="marc__badge ${meta.className}"></span>`,
            mutate(td) {
                td.querySelector('.marc__badge').textContent = t(meta.key, '');
            },
        },
        { text: item.status === 1 ? `+${item.points_awarded}` : '—' },
        { text: item.status === 2 ? (item.reject_reason || '—') : '—' },
        {
            html: '',
            mutate(td) {
                if (item.status !== 2) return;
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'marc__btn marc__btn--link';
                button.textContent = t('editAndResubmit', 'Edit & Resubmit');
                button.addEventListener('click', () => onResubmit?.(item));
                td.appendChild(button);
            },
        },
    ];

    cells.forEach((cell) => {
        const td = document.createElement('td');
        if (cell.text !== undefined) {
            td.textContent = cell.text;
        } else if (cell.html !== undefined) {
            td.innerHTML = cell.html;
        }
        if (cell.attrs) {
            Object.entries(cell.attrs).forEach(([key, value]) => td.setAttribute(key, value));
        }
        cell.mutate?.(td);
        tr.appendChild(td);
    });

    return tr;
};

export const createHistoryTable = (root, { onResubmit } = {}) => {
    const table = byHook('history-table', root);
    const tbody = byHook('history-body', root);
    const loading = byHook('history-loading', root);
    const empty = byHook('history-empty', root);
    const errorEl = byHook('history-error', root);

    const setState = (state) => {
        hide(table);
        hide(loading);
        hide(empty);
        hide(errorEl);
        if (state === 'loading')      show(loading);
        else if (state === 'empty')   show(empty);
        else if (state === 'error')   show(errorEl);
        else if (state === 'data')    show(table);
    };

    return {
        async load() {
            setState('loading');
            try {
                const payload = await api.getHistory();
                const items = payload?.items ?? [];
                if (items.length === 0) {
                    setState('empty');
                    return;
                }
                tbody.replaceChildren(...items.map((item) => renderRow(item, { onResubmit })));
                setState('data');
            } catch (error) {
                if (error instanceof ApiError && error.code === 'login_required') {
                    setState('empty');
                    return;
                }
                setState('error');
            }
        },
    };
};
